// The outline of a card on the table: a rectangle with slightly rounded corners, like a real
// playing card. Everything card-shaped is cut from it: the card itself, the decks, and the tints and
// overlays laid over a card.
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';

/** The corner radius in centimeters, from the setting in pixels of a 500-pixel-wide card picture. */
export function cornerRadius(width = CONFIG.card.width) {
  return Math.max(0, (CONFIG.card.cornerRadius / 500) * width);
}

function outline(w: number, h: number, r: number) {
  const x = -w / 2, y = -h / 2;
  const s = new THREE.Shape();
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
  s.lineTo(x + w, y + h - r);
  s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
  s.lineTo(x + r, y + h);
  s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(x, y + r);
  s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
  return s;
}

/** Stretches the picture over the whole card, exactly as a plain rectangle would. */
function fitUvs(geo: THREE.BufferGeometry, w: number, h: number) {
  const pos = geo.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = pos.getX(i) / w + 0.5;
    uv[i * 2 + 1] = pos.getY(i) / h + 0.5;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** A flat card shape standing in the XY plane, facing +Z (like THREE.PlaneGeometry), with rounded corners. */
export function cardPlane(w: number, h: number, r = cornerRadius(w)): THREE.BufferGeometry {
  if (r <= 0) return new THREE.PlaneGeometry(w, h);
  const geo = new THREE.ShapeGeometry(outline(w, h, Math.min(r, w / 2, h / 2)), 6);
  fitUvs(geo, w, h);
  return geo;
}

/** The same shape lying flat on the table, facing up. */
export function cardFlat(w: number, h: number, r = cornerRadius(w)): THREE.BufferGeometry {
  const geo = cardPlane(w, h, r);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

/** A stack of cards one unit tall (scaled to the pile's height), centered like THREE.BoxGeometry. */
export function cardBlock(w: number, h: number, r = cornerRadius(w)): THREE.BufferGeometry {
  if (r <= 0) return new THREE.BoxGeometry(w, 1, h);
  const geo = new THREE.ExtrudeGeometry(outline(w, h, Math.min(r, w / 2, h / 2)), { depth: 1, bevelEnabled: false, curveSegments: 6 });
  geo.translate(0, 0, -0.5);
  geo.rotateX(-Math.PI / 2);
  return geo;
}
